/**
 * Content Index Store
 * 콘텐츠 인덱싱 상태 및 진행률 관리
 */

import { writable, derived, get } from 'svelte/store'
import type { IndexingProgress, ContentDocument } from '../types/contentIndex'
import { getContentIndexService, type IContentIndexService } from '../services/contentIndex/ContentIndexService'
import { ChangeQueueManager, type FileChange } from '../services/contentIndex/ChangeQueueManager'
import { getFileSystemWatcher } from '../services/FileSystemWatcher'

interface ContentIndexState {
  isInitialized: boolean
  isIndexing: boolean
  progress: IndexingProgress | null
  lastError: string | null
  stats: {
    totalDocuments: number
    indexedDocuments: number
    cacheSize: number
    lastUpdate: number
  }
}

function createContentIndexStore() {
  const { subscribe, set, update } = writable<ContentIndexState>({
    isInitialized: false,
    isIndexing: false,
    progress: null,
    lastError: null,
    stats: {
      totalDocuments: 0,
      indexedDocuments: 0,
      cacheSize: 0,
      lastUpdate: 0
    }
  })
  
  let service: IContentIndexService | null = null
  let changeQueueManager: ChangeQueueManager | null = null
  let fileWatcher = getFileSystemWatcher()
  let watcherListenerId = 'content-index-watcher'
  let initializePromise: Promise<void> | null = null
  let indexingPromise: Promise<void> | null = null
  
  // 서비스 초기화 헬퍼
  async function ensureService(): Promise<IContentIndexService> {
    if (!service) {
      service = await getContentIndexService()
    }
    return service
  }
  
  return {
    subscribe,
    
    /**
     * ContentIndex 초기화
     */
    async initialize(vaultPath: string): Promise<void> {
      // 이미 초기화 중이면 기존 Promise 반환
      if (initializePromise) {
        return initializePromise
      }
      
      // 이미 같은 vault로 초기화되었으면 skip
      const currentState = this.getState()
      if (currentState.isInitialized && service) {
        return Promise.resolve()
      }
      
      initializePromise = this._doInitialize(vaultPath).finally(() => {
        initializePromise = null
      })
      
      return initializePromise
    },
    
    async _doInitialize(vaultPath: string): Promise<void> {
      try {
        // Don't set isInitialized to false if we're already initialized
        // This prevents search from breaking during re-initialization
        const currentState = this.getState()
        if (!currentState.isInitialized) {
          update(state => ({ ...state, isInitialized: false, lastError: null }))
        }
        
        const svc = await ensureService()
        await svc.initialize(vaultPath)
        
        // Initialize change queue manager
        changeQueueManager = new ChangeQueueManager(
          svc,
          (progress) => {
            if (progress) {
              update(state => ({ ...state, progress, isIndexing: true }))
            } else {
              update(state => ({ ...state, progress: null, isIndexing: false }))
            }
          }
        )
        
        // Set up file watcher
        this.setupFileWatcher()
        
        const stats = await svc.getStats()
        
        console.log('[contentIndexStore] Initialization SUCCESS! Setting isInitialized to TRUE')
        update(state => ({
          ...state,
          isInitialized: true,
          stats: {
            totalDocuments: stats.totalDocuments,
            indexedDocuments: stats.totalDocuments,
            cacheSize: stats.indexSize,
            lastUpdate: stats.lastUpdate
          }
        }))
        console.log('[contentIndexStore] State updated, isInitialized should now be TRUE')
      } catch (error) {
        console.error('[contentIndexStore] Initialization FAILED!')
        const errorMessage = error instanceof Error ? error.message : 'Failed to initialize content index'
        update(state => ({
          ...state,
          isInitialized: false,
          lastError: errorMessage
        }))
        console.error('Failed to initialize content index:', error)
      }
    },
    
    /**
     * 전체 인덱싱 수행
     */
    async performFullIndexing(): Promise<void> {
      // 이미 인덱싱 중이면 기존 Promise 반환
      if (indexingPromise) {
        return indexingPromise
      }
      
      indexingPromise = this._doFullIndexing().finally(() => {
        indexingPromise = null
      })
      
      return indexingPromise
    },
    
    async _doFullIndexing(): Promise<void> {
      update(state => ({ ...state, isIndexing: true, lastError: null }))
      
      try {
        const svc = await ensureService()
        await svc.fullIndexing({
          onProgress: (progress) => {
            update(state => ({ ...state, progress }))
          },
          onError: (error) => {
            console.error('Indexing error:', error)
          }
        })
        
        const stats = await svc.getStats()
        
        update(state => ({
          ...state,
          isIndexing: false,
          progress: null,
          stats: {
            totalDocuments: stats.totalDocuments,
            indexedDocuments: stats.totalDocuments,
            cacheSize: stats.indexSize,
            lastUpdate: stats.lastUpdate
          }
        }))
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Indexing failed'
        update(state => ({
          ...state,
          isIndexing: false,
          progress: null,
          lastError: errorMessage
        }))
      }
    },
    
    /**
     * 증분 업데이트
     */
    async performIncrementalUpdate(changedPaths: string[]): Promise<void> {
      if (changedPaths.length === 0) return
      
      update(state => ({ ...state, isIndexing: true }))
      
      try {
        const svc = await ensureService()
        await svc.incrementalUpdate(changedPaths)
        
        const stats = await svc.getStats()
        
        update(state => ({
          ...state,
          isIndexing: false,
          stats: {
            totalDocuments: stats.totalDocuments,
            indexedDocuments: stats.totalDocuments,
            cacheSize: stats.indexSize,
            lastUpdate: stats.lastUpdate
          }
        }))
      } catch (error) {
        console.error('Incremental update failed:', error)
        update(state => ({ ...state, isIndexing: false }))
      }
    },
    
    /**
     * 단일 문서 즉시 업데이트
     * 파일 저장 시 호출되어 즉시 인덱스를 업데이트합니다.
     */
    async updateSingleDocument(path: string, content: string): Promise<void> {
      try {
        const svc = await ensureService()

        // 서비스가 초기화되었는지 확인
        const currentState = this.getState()
        if (!currentState.isInitialized) {
          // 초기화되지 않았으면 업데이트를 skip
          // 초기화를 트리거하면 HMR이 발생하여 전체 앱이 재마운트됨
          console.debug('[ContentIndex] Skipping update - not initialized yet')
          return
        }

        // 직접 문서를 업데이트 (캐시 및 인덱스 모두 갱신)
        await svc.updateDocument(path, content)
        
        // 통계 업데이트
        const stats = await svc.getStats()
        
        update(state => ({
          ...state,
          stats: {
            totalDocuments: stats.totalDocuments,
            indexedDocuments: stats.totalDocuments,
            cacheSize: stats.indexSize,
            lastUpdate: stats.lastUpdate
          }
        }))
        
        console.debug(`Content index updated immediately for: ${path}`)
      } catch (error) {
        console.error('Failed to update single document in index:', error)
        // 실패해도 에러를 throw하지 않음 - 파일 저장은 성공했으므로
      }
    },
    
    /**
     * 캐시 검증
     */
    async validateCache(): Promise<boolean> {
      try {
        const svc = await ensureService()
        const result = await svc.validateCache()

        if (!result.isValid && result.changedFiles) {
          // 변경된 파일이 있으면 증분 업데이트
          const filesToUpdate = [
            ...(result.changedFiles || []),
            ...(result.newFiles || [])
          ]

          if (filesToUpdate.length > 0) {
            await this.performIncrementalUpdate(filesToUpdate)
          }
        }

        return result.isValid
      } catch (error) {
        // 에러 정보를 상세히 로깅
        console.error('[ContentIndexStore] Cache validation failed:', {
          error,
          message: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined
        })
        return false
      }
    },
    
    /**
     * 캐시 초기화
     */
    async clearCache(): Promise<void> {
      try {
        const svc = await ensureService()
        await svc.clearCache()
        
        update(state => ({
          ...state,
          stats: {
            totalDocuments: 0,
            indexedDocuments: 0,
            cacheSize: 0,
            lastUpdate: 0
          }
        }))
      } catch (error) {
        console.error('Failed to clear cache:', error)
      }
    },
    
    /**
     * 검색 실행
     */
    async search(query: string, options?: any) {
      // Check initialization status
      const currentState = this.getState()
      
      if (!currentState.isInitialized) {
        console.error('[ContentIndexStore] Cannot search - store not initialized')
        return []
      }
      
      const svc = await ensureService()
      const searchOptions = {
        query,
        fuzzy: options?.fuzzy ?? true,
        limit: options?.limit ?? 50,
        includeContext: options?.includeContext ?? true,
        ...options
      }
      return svc.search(searchOptions)
    },
    
    /**
     * 파일 변경 감지 설정
     */
    setupFileWatcher(): void {
      // Remove existing listener if any
      fileWatcher.removeListener(watcherListenerId)
      
      // Add new listener
      fileWatcher.onFileChange(watcherListenerId, async () => {
        if (!changeQueueManager) return
        
        try {
          // Get changed files from cache validation
          const svc = await ensureService()
          const validationResult = await svc.validateCache()
          
          if (!validationResult.isValid) {
            const changes: FileChange[] = []
            
            // Handle new files
            if (validationResult.newFiles) {
              for (const path of validationResult.newFiles) {
                changes.push({ path, type: 'created', timestamp: Date.now() })
              }
            }
            
            // Handle changed files
            if (validationResult.changedFiles) {
              for (const path of validationResult.changedFiles) {
                changes.push({ path, type: 'modified', timestamp: Date.now() })
              }
            }
            
            // Handle deleted files
            if (validationResult.deletedFiles) {
              for (const path of validationResult.deletedFiles) {
                changes.push({ path, type: 'deleted', timestamp: Date.now() })
              }
            }
            
            // Add changes to queue
            if (changes.length > 0) {
              changeQueueManager.addChanges(changes)
              
              // Update stats after processing
              setTimeout(async () => {
                const stats = await svc.getStats()
                update(state => ({
                  ...state,
                  stats: {
                    totalDocuments: stats.totalDocuments,
                    indexedDocuments: stats.totalDocuments,
                    cacheSize: stats.indexSize,
                    lastUpdate: stats.lastUpdate
                  }
                }))
              }, 2000)
            }
          }
        } catch (error) {
          console.error('Error handling file changes:', error)
        }
      })
      
      // Start watching
      fileWatcher.start().catch(error => {
        console.error('Failed to start file watcher:', error)
      })
    },
    
    /**
     * 정리 및 종료
     */
    async cleanup(): Promise<void> {
      // Stop file watcher
      fileWatcher.removeListener(watcherListenerId)
      fileWatcher.stop()

      // Clear change queue
      if (changeQueueManager) {
        changeQueueManager.clear()
        changeQueueManager = null
      }

      // Cleanup service (close DB connection, clear caches)
      if (service) {
        await service.cleanup()
        service = null
      }

      // Reset state
      update(state => ({
        ...state,
        isInitialized: false,
        isIndexing: false,
        progress: null
      }))
    },
    
    /**
     * 현재 상태 가져오기
     */
    getState(): ContentIndexState {
      return get({ subscribe })
    },
    
    /**
     * 문서 가져오기
     */
    async getDocument(path: string): Promise<ContentDocument | undefined> {
      const svc = await ensureService()
      return svc.getDocument(path)
    }
  }
}

export const contentIndexStore = createContentIndexStore()

// 디버깅용 - 브라우저 콘솔에서 상태 확인 가능
if (typeof window !== 'undefined') {
  (window as any).contentIndexStore = contentIndexStore
}

// 진행률 정보만 구독하는 파생 스토어
export const indexingProgress = derived(
  contentIndexStore,
  $store => $store.progress
)

// 인덱싱 상태만 구독하는 파생 스토어
export const isIndexing = derived(
  contentIndexStore,
  $store => $store.isIndexing
)

// 통계 정보만 구독하는 파생 스토어
export const contentIndexStats = derived(
  contentIndexStore,
  $store => $store.stats
)